/**
 * Feature Flags Configuration
 *
 * Control feature availability and gating.
 * Update these flags to enable/disable features without code changes.
 */

export const FEATURES = {
  /**
   * Insight Modal Gating
   *
   * When enabled, users must have a paid subscription to view detailed insights.
   * When disabled, all users can view detailed insights (early adopter mode).
   *
   * Free tier: See titles and summaries
   * Paid tier: Click to see full strategic + tactical details
   *
   * To enable: Set to true
   * To disable: Set to false
   */
  REQUIRE_SUBSCRIPTION_FOR_INSIGHTS: false,

  /**
   * Free Insight Views Limit
   *
   * Number of detailed insights free users can view before being prompted to upgrade.
   * Only applies when REQUIRE_SUBSCRIPTION_FOR_INSIGHTS is true.
   * Set to 0 to require immediate upgrade.
   * Set to null for unlimited views.
   */
  FREE_INSIGHT_VIEWS_LIMIT: 3,
} as const

/**
 * Helper to check if a user can view detailed insights
 */
export function canViewDetailedInsights(
  userSubscriptionTier: string | undefined,
  freeViewsRemaining?: number
): { allowed: boolean; reason?: string } {
  // Feature gating is disabled - everyone can view
  if (!FEATURES.REQUIRE_SUBSCRIPTION_FOR_INSIGHTS) {
    return { allowed: true }
  }

  // User has a paid subscription
  if (userSubscriptionTier && userSubscriptionTier !== 'free') {
    return { allowed: true }
  }

  // Check free views limit
  if (FEATURES.FREE_INSIGHT_VIEWS_LIMIT !== null) {
    if (freeViewsRemaining === undefined || freeViewsRemaining > 0) {
      return { allowed: true, reason: 'free_view' }
    }
    return { allowed: false, reason: 'limit_reached' }
  }

  // Default: require subscription
  return { allowed: false, reason: 'subscription_required' }
}
